import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/apiConfig';
import './HyperliquidPositionsWidget.css';

/**
 * Compact widget to display top Hyperliquid positions for a specific coin
 * Used on CoinPage to show whale positions
 */
const HyperliquidPositionsWidget = ({ symbol, limit = 3 }) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPositions = async () => {
      if (!symbol) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch top positions by size for this symbol
        const res = await fetch(
          `${API_BASE_URL}/api/hyperliquid/positions?symbol=${symbol}&limit=${limit}&sortBy=size&minSize=0`
        );

        if (!res.ok) {
          throw new Error('Failed to fetch positions');
        }

        const data = await res.json();
        setPositions(data.positions || []);
      } catch (err) {
        console.error('Failed to fetch Hyperliquid positions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPositions();

    // Refresh every 60 seconds
    const interval = setInterval(fetchPositions, 60000);
    return () => clearInterval(interval);
  }, [symbol, limit]);

  // Format wallet address to short form
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '-';
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(num) >= 1000) {
      return `$${(num / 1000).toFixed(1)}K`;
    }
    return `$${num.toFixed(0)}`;
  };

  // Format PnL with sign
  const formatPnl = (pnl) => {
    if (pnl === undefined || pnl === null) return '-';
    const sign = pnl >= 0 ? '+' : '';
    if (Math.abs(pnl) >= 1000000) {
      return `${sign}$${(pnl / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(pnl) >= 1000) {
      return `${sign}$${(pnl / 1000).toFixed(1)}K`;
    }
    return `${sign}$${pnl.toFixed(0)}`;
  };

  // Format ROE percentage
  const formatRoe = (roe) => {
    if (roe === undefined || roe === null) return '';
    const sign = roe >= 0 ? '+' : '';
    return `(${sign}${(roe * 100).toFixed(2)}%)`;
  };

  if (loading) {
    return (
      <div className="hl-positions-widget">
        <div className="hl-widget-header">
          <span className="hl-widget-title">{t('hlPositionsWidget.title', 'Active Hyperliquid Positions')}</span>
        </div>
        <div className="hl-widget-loading">{t('hlPositionsWidget.loading', 'Loading positions...')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hl-positions-widget">
        <div className="hl-widget-header">
          <span className="hl-widget-title">{t('hlPositionsWidget.title', 'Active Hyperliquid Positions')}</span>
        </div>
        <div className="hl-widget-error">{t('hlPositionsWidget.error', 'Failed to load positions')}</div>
      </div>
    );
  }

  return (
    <div className="hl-positions-widget">
      <div className="hl-widget-header">
        <div className="hl-widget-title-row">
          <span className="hl-live-indicator"></span>
          <span className="hl-widget-title">{t('hlPositionsWidget.title', 'Active Hyperliquid Positions')}</span>
        </div>
        <Link to={`/hyperliquid?symbol=${symbol}`} className="hl-widget-link">
          {t('hlPositionsWidget.allPositions', 'All Positions')} &rarr;
        </Link>
      </div>

      {positions.length === 0 ? (
        <div className="hl-widget-empty">
          {t('hlPositionsWidget.noPositions', 'No active positions for {{symbol}}', { symbol })}
        </div>
      ) : (
        <div className="hl-widget-table">
          <div className="hl-table-header">
            <span className="hl-col-trader">{t('hlPositionsWidget.trader', 'TRADER')}</span>
            <span className="hl-col-side">{t('hlPositionsWidget.side', 'SIDE')}</span>
            <span className="hl-col-size">{t('hlPositionsWidget.size', 'SIZE')}</span>
            <span className="hl-col-pnl">{t('hlPositionsWidget.pnl', 'PNL (ROE%)')}</span>
          </div>

          {positions.map((pos) => (
            <Link
              key={`${pos.user}-${pos.symbol}`}
              to={`/hyperliquid/wallet/${pos.user}`}
              className="hl-table-row"
            >
              <div className="hl-col-trader">
                <span className="hl-address">{formatAddress(pos.user)}</span>
                <span className="hl-leverage">{pos.leverage}x</span>
              </div>
              <div className="hl-col-side">
                <span className={`hl-side-badge ${pos.side}`}>
                  {pos.side?.toUpperCase()}
                </span>
              </div>
              <div className="hl-col-size">
                {formatNumber(pos.positionValue)}
              </div>
              <div className={`hl-col-pnl ${pos.unrealizedPnl >= 0 ? 'profit' : 'loss'}`}>
                <span className="hl-pnl-value">{formatPnl(pos.unrealizedPnl)}</span>
                <span className="hl-pnl-roe">{formatRoe(pos.returnOnEquity)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default HyperliquidPositionsWidget;
